import { useEffect, useState } from 'react'
import { Button, Table, Select } from '../../components/ui'
import { slideInsertsAPI, insertError } from './api'
import { CARD_STATUS_LABELS, INSERT_REASON_LABELS, type InsertCard } from './types'

export function SlideInsertCardsTable({
  insertId,
  canUpdate,
  canSend,
  onSend,
  onJob,
  revision = 0,
  initialStatus = '',
}: {
  insertId: number
  canUpdate: boolean
  canSend: boolean
  onSend: (ids: number[], rows: InsertCard[]) => void
  onJob: (id: string) => void
  revision?: number
  initialStatus?: string
}) {
  const [items, setItems] = useState<InsertCard[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [status, setStatus] = useState(initialStatus)
  useEffect(() => {
    setStatus(initialStatus)
    setPage(0)
  }, [initialStatus])
  const [selected, setSelected] = useState<number[]>([])
  const [reload, setReload] = useState(0)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    let alive = true
    setLoading(true)
    setSelected([])
    void slideInsertsAPI
      .cards(insertId, { status: status || undefined, limit: 50, offset: page * 50 })
      .then((data) => {
        if (alive) {
          setItems(data.items)
          setTotal(data.total)
          setError('')
        }
      })
      .catch((e) => {
        if (alive) setError(insertError(e))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [insertId, page, status, reload, revision])
  async function toggle(row: InsertCard) {
    try {
      const result = await slideInsertsAPI.setExcluded(
        insertId,
        row.card_id,
        row.status !== 'excluded',
      )
      if (result.jobId) onJob(result.jobId)
      else setReload((n) => n + 1)
    } catch (e) {
      setError(insertError(e))
    }
  }
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Select
          aria-label="Состояние карточек"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value)
            setPage(0)
          }}
        >
          <option value="">Все состояния</option>
          {Object.entries(CARD_STATUS_LABELS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </Select>
        {canSend && (
          <Button
            disabled={!selected.length}
            onClick={() =>
              onSend(
                selected,
                items.filter((row) => selected.includes(row.card_id)),
              )
            }
          >
            Отправить выбранные ({selected.length})
          </Button>
        )}
      </div>
      {error && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
      <Table
        data={items}
        loading={loading}
        keyExtractor={(row) => row.card_id}
        columns={[
          {
            key: 'select',
            header: '',
            render: (_, row) =>
              row.status === 'pending' && canSend ? (
                <input
                  type="checkbox"
                  aria-label={`Выбрать ${row.article}`}
                  checked={selected.includes(row.card_id)}
                  onChange={(e) =>
                    setSelected((ids) =>
                      e.target.checked
                        ? [...ids, row.card_id]
                        : ids.filter((id) => id !== row.card_id),
                    )
                  }
                />
              ) : null,
          },
          {
            key: 'article',
            header: 'Артикул',
            render: (_, row) => <code className="font-mono">{row.article}</code>,
          },
          { key: 'marketplace', header: 'Площадка' },
          { key: 'seller_name', header: 'Кабинет' },
          {
            key: 'status',
            header: 'Состояние',
            render: (_, row) => CARD_STATUS_LABELS[row.status] || row.status,
          },
          {
            key: 'reason',
            header: 'Причина',
            render: (_, row) => (row.reason ? INSERT_REASON_LABELS[row.reason] || row.reason : '—'),
          },
          {
            key: 'send_status',
            header: 'Отправка',
            render: (_, row) =>
              row.send_status === 'not_needed'
                ? 'Не требуется'
                : CARD_STATUS_LABELS[row.send_status || ''] || row.send_status || '—',
          },
          {
            key: 'action',
            header: '',
            render: (_, row) =>
              canUpdate && (
                <Button size="sm" variant="secondary" onClick={() => void toggle(row)}>
                  {row.status === 'excluded' ? 'Вернуть' : 'Исключить'}
                </Button>
              ),
          },
        ]}
      />
      <div className="flex justify-between gap-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={!page}
          onClick={() => setPage((n) => n - 1)}
        >
          Назад
        </Button>
        <span className="text-sm">
          {page * 50 + (items.length ? 1 : 0)}–{page * 50 + items.length} из {total}
        </span>
        <Button
          size="sm"
          variant="secondary"
          disabled={(page + 1) * 50 >= total}
          onClick={() => setPage((n) => n + 1)}
        >
          Далее
        </Button>
      </div>
    </div>
  )
}
